'use client';

import { useEffect, useState, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';

export default function SimpleAppRouterProgressProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isNavigating, setIsNavigating] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    // Reset navigation state when pathname changes
    setIsNavigating(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [pathname]);

  useEffect(() => {
    // Override router methods to show progress
    const originalPush = router.push;
    const originalReplace = router.replace;
    const originalBack = router.back;
    const originalForward = router.forward;

    const showProgress = () => {
      setIsNavigating(true);
      // Fallback timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsNavigating(false);
      }, 3000);
    };

    router.push = (...args) => {
      showProgress();
      return originalPush.apply(router, args);
    };

    router.replace = (...args) => {
      showProgress();
      return originalReplace.apply(router, args);
    };

    router.back = () => {
      showProgress();
      return originalBack.apply(router);
    };

    router.forward = () => {
      showProgress();
      return originalForward.apply(router, []);
    };

    // Also listen for Link clicks
    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;

      if (
        link &&
        link.href &&
        !link.href.startsWith('mailto:') &&
        !link.href.startsWith('tel:')
      ) {
        const url = new URL(link.href);
        if (url.origin === window.location.origin) {
          showProgress();
        }
      }
    };

    document.addEventListener('click', handleLinkClick);

    return () => {
      router.push = originalPush;
      router.replace = originalReplace;
      router.back = originalBack;
      router.forward = originalForward;
      document.removeEventListener('click', handleLinkClick);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [router]);

  return (
    <>
      {isNavigating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/5 backdrop-blur-xs">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-el-gray-200 border-t-el-blue-500" />
        </div>
      )}
      {children}
    </>
  );
}
